import React from 'react';
import { AccountStatus } from '@/components/AccountStatus';
import { SiteFooter } from '@/components/SiteFooter';
import { ExploreTab } from '@/features/explore/ExploreTab';
import type { PromptInspirationItem } from '@/features/explore/types';
import { WorkoutHomeHeader } from './WorkoutHomeHeader';
import { WorkoutHomeSectionTabs, type HomeAppSection } from './WorkoutHomeSectionTabs';
import { WorkoutSportTabs } from './WorkoutSportTabs';
import { WorkoutPromptColumn } from './WorkoutPromptColumn';
import { WorkoutVisualizerColumn } from './WorkoutVisualizerColumn';
import { WorkoutExportPanel } from './WorkoutExportPanel';
import { WorkoutHistoryPanel } from './WorkoutHistoryPanel';
import type { WorkoutHomeHeaderProps } from './WorkoutHomeHeader';
import type { WorkoutPromptColumnProps } from './WorkoutPromptColumn';
import type { WorkoutVisualizerColumnProps } from './WorkoutVisualizerColumn';
import type { WorkoutExportPanelProps } from './WorkoutExportPanel';
import type { WorkoutHistoryPanelProps } from './WorkoutHistoryPanel';
import type { WorkoutSportTabsProps } from './WorkoutSportTabs';

export interface WorkoutHomeExploreNavProps {
  section: HomeAppSection;
  setSection: (next: HomeAppSection) => void;
  onUseInspirationTemplate: (item: PromptInspirationItem) => void;
}

export interface WorkoutHomeLayoutProps {
  isPro: boolean;
  guestGenerations: number;
  header: WorkoutHomeHeaderProps;
  exploreNav: WorkoutHomeExploreNavProps;
  sportTabs: WorkoutSportTabsProps;
  prompt: WorkoutPromptColumnProps;
  visualizer: WorkoutVisualizerColumnProps;
  exportPanel: WorkoutExportPanelProps;
  history: WorkoutHistoryPanelProps;
}

/**
 * Main builder shell: account banner, header, sport tabs, prompt + chart grid, export area, history, footer.
 */
export function WorkoutHomeLayout({
  isPro,
  guestGenerations,
  header,
  exploreNav,
  sportTabs,
  prompt,
  visualizer,
  exportPanel,
  history,
}: WorkoutHomeLayoutProps) {
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <AccountStatus isPro={isPro} guestGenerations={guestGenerations} language={header.language} />
      <WorkoutHomeHeader {...header} />
      <main className="space-y-6">
        <WorkoutHomeSectionTabs section={exploreNav.section} setSection={exploreNav.setSection} t={header.t} />
        {exploreNav.section === 'builder' ? (
          <>
            <WorkoutSportTabs {...sportTabs} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <WorkoutPromptColumn {...prompt} />
              <WorkoutVisualizerColumn {...visualizer} />
            </div>
            <WorkoutExportPanel {...exportPanel} />
            <WorkoutHistoryPanel {...history} />
          </>
        ) : (
          <ExploreTab
            t={header.t}
            onUseTemplate={exploreNav.onUseInspirationTemplate}
            onGoToBuilder={() => exploreNav.setSection('builder')}
          />
        )}
      </main>
      <SiteFooter t={header.t} />
    </div>
  );
}
